import { useState } from "react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Tooltip state
// ---------------------------------------------------------------------------

interface TooltipInfo {
  x: number;
  y: number;
  label: string;
  value: number;
}

// ---------------------------------------------------------------------------
// BarChart SVG interativo com tooltip
// ---------------------------------------------------------------------------

interface BarChartProps {
  values: number[];
  labels?: string[];
  color?: string;
  className?: string;
}

function fmt(n: number): string {
  return n.toLocaleString("pt-BR");
}

export function BarChart({
  values,
  labels,
  color = "hsl(150 54% 51%)",
  className,
}: BarChartProps): JSX.Element {
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const max = Math.max(...values, 1);
  const barCount = values.length;
  const svgW = 600;
  const svgH = 80;
  const gap = 2;
  const barW = (svgW - gap * (barCount - 1)) / barCount;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        className={cn("w-full", className)}
        preserveAspectRatio="none"
        aria-hidden="true"
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id="bar-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.9" />
            <stop offset="100%" stopColor={color} stopOpacity="0.25" />
          </linearGradient>
        </defs>
        {values.map((v, i) => {
          const barH = (v / max) * svgH;
          const x = i * (barW + gap);
          const y = svgH - barH;
          const isLast = i === values.length - 1;
          const centerX = x + barW / 2;

          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(barH, 1)}
                rx={1.5}
                fill={isLast ? color : "url(#bar-fill)"}
                opacity={isLast ? 1 : 0.8}
              />
              {/* Overlay de captura */}
              <rect
                x={x}
                y={0}
                width={barW}
                height={svgH}
                fill="transparent"
                className="cursor-crosshair"
                onMouseEnter={(e) => {
                  const rect = (e.currentTarget.closest("svg") as SVGSVGElement).getBoundingClientRect();
                  const scaleX = rect.width / svgW;
                  setTooltip({
                    x: centerX * scaleX,
                    y: 0,
                    label: labels?.[i] ?? `Dia ${i + 1}`,
                    value: v,
                  });
                }}
              />
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="pointer-events-none absolute -top-9 z-10 -translate-x-1/2 rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-2 py-1 shadow-md"
          style={{ left: tooltip.x }}
        >
          <p className="text-[10px] text-muted-foreground whitespace-nowrap">{tooltip.label}</p>
          <p className="text-xs font-bold tabular-nums text-foreground text-center">{fmt(tooltip.value)}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AxisLabels - rotulos de eixo X para o grafico
// ---------------------------------------------------------------------------

interface AxisLabelsProps {
  labels: string[];
}

export function AxisLabels({ labels }: AxisLabelsProps): JSX.Element {
  // Mostrar apenas primeiro, meio e ultimo para nao poluir
  const first = labels[0] ?? "";
  const mid = labels[Math.floor(labels.length / 2)] ?? "";
  const last = labels[labels.length - 1] ?? "";

  return (
    <div className="flex justify-between mt-1.5 px-0.5">
      <span className="text-[10px] text-muted-foreground font-mono">{first}</span>
      {labels.length > 2 && (
        <span className="text-[10px] text-muted-foreground font-mono">{mid}</span>
      )}
      <span className="text-[10px] text-muted-foreground font-mono">{last}</span>
    </div>
  );
}
